import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RealEstateDto } from "api/real-estate";
import { NsKey } from "enums/NsKey";
import { ParcEligibilitySelection } from "../components/real-estate/ParcEligibilityPanel";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { realEstateAPI } from "services/swagger-api/realEstateAPI";
import { RGIStepIdentifier, UPIStepIdentifier } from "features/subscription-process/types/enums";
import { ContextCode, SurveyName, useGenericSurvey } from "features/subscription-process/profiling-process/hooks/useGenericSurvey";
import { BranchCode } from "api/risk";
import { risksAPI } from "services/swagger-api/riskApi";
import { ProfilingMode } from "features/subscription-process/profiling-process/slices/types/enums";
import { useRgiRiskData, useUpiRiskData } from "features/subscription-process/contexts/selectors/selectors";
import { BranchStepConfigFactory, createStepConfig } from "features/subscription-process/navigation/Steps/createStepConfig";

export const useRealEstateRegistration = (branchCode: BranchCode) => {
  // fetchSubscriptionProcess is called by SubscriptionProcessWrapper - no need to call it here
  const { riskId } = useParams<{ riskId: string }>();

  const { t } = useDynamicTranslation([NsKey.RealEstate, NsKey.Common]);
  const [isSelectedRealEstateEligible, setIsSelectRealEstateEligible] = useState(false)
  const [parcSelection, setParcSelection] = useState<ParcEligibilitySelection>({
    isValid: false,
    includedRealEstateIds: []
  })
  const { initSurvey } = useGenericSurvey()
  const upiRiskData = useUpiRiskData()
  const rgiRiskData = useRgiRiskData()
  const person = branchCode === BranchCode.RGI ? rgiRiskData?.person : upiRiskData?.person
  const realEstate = branchCode === BranchCode.RGI ? rgiRiskData?.realEstate : upiRiskData?.realEstate
  const navigate = useNavigate();

  // États locaux
  const [selectedRealEstateId, setSelectedRealEstateId] = useState('');
  const [selectedParcId, setSelectedParcId] = useState('');
  const [realEstates, setRealEstates] = useState<RealEstateDto[]>([]);
  const [realEstatesTotal, setRealEstatesTotal] = useState(0);
  const [realEstatesPage, setRealEstatesPage] = useState(1);
  const [realEstatesSearch, setRealEstatesSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [postSubmit, setPostSubmit] = useState<(() => Promise<void>) | undefined>()
  const [eligibilityRefreshKey, setEligibilityRefreshKey] = useState(0)

  // Le bien sélectionné peut ne plus être dans la page courante (liste paginée serveur) :
  // on le récupère alors par id pour garder l'encart d'éligibilité fonctionnel.
  const [selectedRealEstateFetched, setSelectedRealEstateFetched] = useState<RealEstateDto | undefined>(undefined);

  const selectedRealEstate = useMemo(() => {
    const inPage = realEstates.find((re) => re.id === selectedRealEstateId);
    if (inPage) return inPage;
    return selectedRealEstateFetched?.id === selectedRealEstateId ? selectedRealEstateFetched : undefined;
  }, [realEstates, selectedRealEstateId, selectedRealEstateFetched])

  useEffect(() => {
    if (realEstate) {
      setSelectedRealEstateId(realEstate.id);
    }
  }, [realEstate]);

  useEffect(() => {
    if (!selectedRealEstateId || realEstates.some((re) => re.id === selectedRealEstateId)) {
      return;
    }
    let cancelled = false;
    realEstateAPI
      .getRealEstateById({ bienId: selectedRealEstateId, includeQuestionnaire: false, codeBranche: '' })
      .then((res) => { if (!cancelled) setSelectedRealEstateFetched(res.realEstate); })
      .catch((err) => console.error('Erreur lors du chargement du bien sélectionné:', err));
    return () => { cancelled = true; };
  }, [selectedRealEstateId, realEstates]);

  const REAL_ESTATES_PAGE_SIZE = 10;

  const loadRealEstates = useCallback(async () => {
    if (!person?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await realEstateAPI.searchBiensByProprietaire({
        personneId: person.id,
        getGenericListCommandDto: {
          searchString: realEstatesSearch,
          pageNumber: realEstatesPage,
          pageSize: REAL_ESTATES_PAGE_SIZE
        }
      });
      setRealEstates(response.items ?? []);
      setRealEstatesTotal(response.totalCount ?? 0);
      // Increment refresh key to trigger eligibility re-fetch
      setEligibilityRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Erreur lors du chargement des biens immobiliers:', error);
      setError('Erreur lors du chargement des biens immobiliers');
    } finally {
      setIsLoading(false);
    }
  }, [person?.id, realEstatesPage, realEstatesSearch]);

  useEffect(() => {
    loadRealEstates();
  }, [loadRealEstates]);

  // Recherche serveur : on revient en page 1 à chaque changement de filtre.
  const onRealEstatesSearchChange = useCallback((search: string) => {
    setRealEstatesSearch(search);
    setRealEstatesPage(1);
  }, []);

  const goToNextStep = useCallback(async () => {
    // Continuer avec un parc : lier le parc au risque de simulation puis avancer.
    // GLI → dossier location (saisie des locataires bien par bien) ; CBNO → tarificateur (pas de locataires).
    if (selectedParcId) {
      if (!riskId) return;
      try {
        const response = await risksAPI.linkParcToRisque({
          linkParcToRisqueCommandDto: {
            riskId,
            parcId: selectedParcId,
            includedRealEstateIds: parcSelection.includedRealEstateIds,
            effectiveDate: parcSelection.effectiveDate ? new Date(parcSelection.effectiveDate) : undefined
          }
        });
        if (response.isSuccess) {
          if (branchCode === BranchCode.RGI) {
            navigate(`/simulation/inscription/tenants/${riskId}`);
          } else {
            navigate(`/simulation/tarification/${riskId}`);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la liaison du parc au risque:', error);
      }
      return;
    }
    try {
      const response = await risksAPI.linkRealEstate({
        linkRealEstateCommandDto: {
          realEstateId: selectedRealEstateId,
          riskId
        }
      });

      if (response.isSuccess) {
        if (branchCode === BranchCode.RGI) {
          // Attendre que postSubmit se termine avant de naviguer
          if (postSubmit) {
            await postSubmit();
          }
          navigate(`/simulation/inscription/tenants/${riskId}`);
        } else {
          navigate(`/simulation/tarification/${riskId}`);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la navigation:', error);
      // Optionnel : afficher un message d'erreur à l'utilisateur
    }
  }, [navigate, selectedRealEstateId, selectedParcId, parcSelection, riskId, branchCode, postSubmit]);

  const stepPageConfig: Partial<Record<BranchCode, BranchStepConfigFactory>> = useMemo(() => ({
    [BranchCode.UPI]: createStepConfig({
      next: {
        identifier: UPIStepIdentifier.Pricing,
        route: "/simulation/tarification",
        label: "Aller au tarificateur",
        disabled: (!selectedRealEstateId && !selectedParcId) || (!!selectedParcId && !parcSelection.isValid),
        onClick: goToNextStep,
      },
      prev: {
        identifier: UPIStepIdentifier.Registration,
        route: "/simulation/inscription/cbno",
      },
    }),
    [BranchCode.RGI]: createStepConfig({
      next: {
        identifier: RGIStepIdentifier.TenantRegistration,
        route: "/simulation/inscription/tenants",
        disabled:
          (!selectedParcId && (!selectedRealEstateId || !isSelectedRealEstateEligible)) ||
          (!!selectedParcId && !parcSelection.isValid),
        onClick: goToNextStep,
      },
      prev: {
        identifier: RGIStepIdentifier.Registration,
        route: "/simulation/inscription/gli",
      },
    })
  }), [goToNextStep, isSelectedRealEstateEligible, parcSelection.isValid, selectedRealEstateId, selectedParcId]);

  const resetParcSelection = useCallback(() => {
    setParcSelection({ isValid: false, includedRealEstateIds: [] });
  }, []);

  const onSelectRealEstate = useCallback((realEstateId: string) => {
    setSelectedRealEstateId(realEstateId);
    setSelectedParcId('');
    resetParcSelection();
  }, [resetParcSelection]);

  const onSelectParc = useCallback((parcId: string) => {
    setSelectedParcId(parcId);
    setSelectedRealEstateId('');
    resetParcSelection();
  }, [resetParcSelection]);

  const clearSelection = useCallback(() => {
    setSelectedRealEstateId('');
    setSelectedParcId('');
    resetParcSelection();
  }, [resetParcSelection]);

  const onParcSelectionChange = useCallback((selection: ParcEligibilitySelection) => {
    setParcSelection(selection);
  }, []);

  useEffect(() => {
    initSurvey(ContextCode.RealEstate, SurveyName.RealEstate, ProfilingMode.AutoAdvance)
  }, [initSurvey]);

  const onEligibilityChange = useCallback((eligibility: boolean) => {
    setIsSelectRealEstateEligible(eligibility)
  }, [])

  return {
    // États
    onEligibilityChange,
    isSelectedRealEstateEligible,
    selectedRealEstateId,
    selectedParcId,
    realEstates,
    realEstatesTotal,
    realEstatesPage,
    realEstatesSearch,
    realEstatesPageSize: REAL_ESTATES_PAGE_SIZE,
    isLoading,
    error,
    eligibilityRefreshKey,

    // Actions
    onSelectRealEstate,
    onSelectParc,
    onParcSelectionChange,
    clearSelection,
    loadRealEstates,
    setRealEstatesPage,
    onRealEstatesSearchChange,
    goToNextStep,

    // Configurations (sans JSX)
    stepPageConfig,

    // Utilitaires
    t,
    setPostSubmit,
    selectedRealEstate,
    riskId,
    personId: person?.id
  };
};
